import React, { useCallback } from "react";
import { AppShell, usePolling } from "@/components/AppShell";
import { KpiCard, Skeleton } from "@/components/EmptyState";
import { api } from "@/lib/api";
import {
  Euro, Factory, Boxes, AlertTriangle, ShoppingCart, Activity, PackagePlus, PackageMinus,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, LineChart, Line, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { HealthScoreCard } from "@/components/HealthScoreCard";
import { DashboardWidgets } from "@/components/DashboardWidgets";

const CHART_COLORS = ["hsl(221,100%,59%)", "hsl(160,60%,40%)", "hsl(35,90%,58%)", "hsl(200,90%,55%)", "hsl(0,72%,55%)"];

export default function Dashboard() {
  const fetchAll = useCallback(async () => {
    const [kpis, charts, activity, alerts] = await Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/charts"),
      api.get("/dashboard/activity?limit=15"),
      api.get("/dashboard/alerts"),
    ]);
    return {
      kpis: kpis.data, charts: charts.data,
      activity: activity.data.activity, alerts: alerts.data,
    };
  }, []);

  const { data, loading, polling, lastUpdated } = usePolling(fetchAll, 15000);

  return (
    <AppShell title="Dashboard" subtitle="Real-time operational overview — auto-refreshes every 15s" polling={polling} lastUpdatedMs={lastUpdated}
      headerActions={(
        <Button asChild size="sm" variant="secondary" className="hidden sm:inline-flex">
          <Link to="/app/inventory">Go to inventory</Link>
        </Button>
      )}
    >
      {loading && !data ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <>
          {/* Inventory Health Score — pinned at top alongside the KPI strip */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-1"><HealthScoreCard /></div>
            <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-3 gap-3 content-start">
              <KpiCard label="Inventory Value" value={`€${(data?.kpis?.total_inventory_value || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`} icon={Euro} hint={`${data?.kpis?.total_skus || 0} SKUs`} />
              <KpiCard label="Open Orders" value={data?.kpis?.open_production_orders || 0} icon={Factory} hint="Draft + In progress" />
              <KpiCard label="Units Produced (30d)" value={data?.kpis?.units_produced_30d || 0} icon={Boxes} />
              <KpiCard label="Low Stock" value={data?.kpis?.low_stock_count || 0} icon={AlertTriangle} accent="bg-amber-500/10 text-amber-600 dark:text-amber-400" hint="Below minimum" />
              <KpiCard label="Pending Reorders" value={data?.kpis?.pending_purchases || 0} icon={ShoppingCart} hint="Total reorder qty" />
            </div>
          </div>

          {/* Phase 7 Increment 2 — Operational widgets */}
          <div className="mt-3">
            <DashboardWidgets />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <ChartCard title="Inventory by category">
              {(data?.charts?.inventory_by_category?.length || 0) === 0 ? (
                <EmptyChart label="No inventory yet" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.charts.inventory_by_category}
                      dataKey="value"
                      nameKey="category"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={2}
                      stroke="hsl(var(--background))"
                    >
                      {data.charts.inventory_by_category.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Production status">
              {(data?.charts?.production_by_status?.reduce((a, b) => a + b.count, 0) || 0) === 0 ? (
                <EmptyChart label="No production orders yet" />
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data?.charts?.production_by_status || []}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                    <XAxis dataKey="status" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="hsl(var(--chart-1))" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <ChartCard title="Production units (14d)">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data?.charts?.production_progress || []}>
                  <defs>
                    <linearGradient id="opProd" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="units" stroke="hsl(var(--chart-1))" fill="url(#opProd)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Top stock levels">
              {(data?.charts?.stock_levels?.length || 0) === 0 ? (
                <EmptyChart label="No items yet" />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={data.charts.stock_levels}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.6} vertical={false} />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line type="monotone" dataKey="qty" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="min" stroke="hsl(var(--chart-3))" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading font-semibold flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> Activity feed</div>
              </div>
              <div className="divide-y divide-border">
                {(data?.activity || []).length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">No recent activity</div>}
                {(data?.activity || []).map((log) => (
                  <div key={log.id} className="py-2.5 flex items-center gap-3 text-sm">
                    <ActivityIcon type={log.action} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">
                        <span className="text-muted-foreground">{log.user_email}</span> <span className="font-medium capitalize">{log.action.replace("_", " ")}</span> on <span className="font-mono text-xs">{log.resource}</span>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground shrink-0">{fmtTs(log.timestamp)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="font-heading font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Alerts</div>
              <div className="mt-3 space-y-2">
                {[...(data?.alerts?.critical || []), ...(data?.alerts?.low_stock || [])].slice(0, 6).map((a) => (
                  <div key={a.item_id} className="rounded-lg border border-border px-3 py-2 flex items-center justify-between text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{a.sku}</div>
                    </div>
                    <div className={a.quantity <= 0 ? "text-destructive font-medium" : "text-amber-600 dark:text-amber-400 font-medium"}>
                      {a.quantity} / {a.min_stock}
                    </div>
                  </div>
                ))}
                {(((data?.alerts?.critical?.length || 0) + (data?.alerts?.low_stock?.length || 0)) === 0) && (
                  <div className="py-6 text-sm text-muted-foreground text-center">All good — no low-stock items.</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

function ActivityIcon({ type }) {
  const base = "w-7 h-7 rounded-full flex items-center justify-center shrink-0";
  if (type === "intake") return <div className={`${base} bg-emerald-500/10 text-emerald-500`}><PackagePlus className="w-4 h-4" /></div>;
  if (type === "outtake") return <div className={`${base} bg-amber-500/10 text-amber-500`}><PackageMinus className="w-4 h-4" /></div>;
  if (type === "status_change") return <div className={`${base} bg-primary/10 text-primary`}><Factory className="w-4 h-4" /></div>;
  return <div className={`${base} bg-muted text-muted-foreground`}><Activity className="w-4 h-4" /></div>;
}

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="font-heading font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function EmptyChart({ label }) {
  return <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">{label}</div>;
}

const tooltipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
  borderRadius: 8, color: "hsl(var(--popover-foreground))", fontSize: 12,
};

function fmtTs(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }); }
  catch { return s; }
}
